import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { BRAND, NAV } from "../lib/site";
import { useSettings } from "../lib/SettingsContext";

export default function Footer() {
  const s = useSettings();
  const phone = s.phone || BRAND.phone;
  const email = s.email || BRAND.email;
  const whatsapp = (s.whatsapp || BRAND.whatsapp).replace(/\D/g, "");
  const address = s.address || BRAND.address;
  const footerInfo = s.footer_info || `© ${new Date().getFullYear()} Beyond The Stage Academy. All rights reserved.`;
  const socials = [
    { Icon: Instagram, url: s.social_instagram },
    { Icon: Facebook, url: s.social_facebook },
    { Icon: Youtube, url: s.social_youtube },
    { Icon: Linkedin, url: s.social_linkedin },
  ].filter((x) => x.url);
  const socialFallback = [Instagram, Facebook, Youtube];
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#080808] border-t border-white/10 mt-32"
    >
      <div className="gold-divider" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Link to="/" className="flex items-center gap-4">
            <img src={BRAND.logo_mark} alt={BRAND.name} className="h-16 w-16 object-contain mix-blend-screen" />
            <div>
              <div className="font-serif text-2xl text-white tracking-wide">
                Beyond The Stage
              </div>
              <div className="font-sans text-[10px] tracking-[0.36em] uppercase gold-text">
                Academy
              </div>
            </div>
          </Link>
          <p className="mt-6 font-sans text-[15px] leading-relaxed text-white/55 max-w-md">
            A premium enrichment academy helping children and teens
            develop confidence, communication and character through experiential
            learning.
          </p>
          <p className="mt-8 section-label">{BRAND.tagline}</p>
        </div>

        <div className="lg:col-span-3">
          <h4 className="section-label mb-6">Navigate</h4>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-link-${n.label.toLowerCase()}`}
                  className="font-sans text-sm text-white/70 hover:text-[#d4af37] transition-colors"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="section-label mb-6">Contact</h4>
          <ul className="space-y-4 font-sans text-sm text-white/70">
            <li className="flex items-start gap-3">
              <Phone size={15} className="text-[#d4af37] mt-1 shrink-0" />
              <a
                data-testid="footer-phone"
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="hover:text-white"
              >
                {phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={15} className="text-[#d4af37] mt-1 shrink-0" />
              <a
                data-testid="footer-email"
                href={`mailto:${email}`}
                className="hover:text-white break-all"
              >
                {email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle size={15} className="text-[#d4af37] mt-1 shrink-0" />
              <a
                data-testid="footer-whatsapp"
                href={`https://wa.me/${whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white"
              >
                WhatsApp Us
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-[#d4af37] mt-1 shrink-0" />
              <span>{address}</span>
            </li>
          </ul>
          <div className="mt-8 flex gap-3">
            {(socials.length ? socials : socialFallback.map((Icon) => ({ Icon, url: "#" }))).map(({ Icon, url }, i) => (
              <a
                key={i}
                href={url}
                target={url !== "#" ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={`footer-social-${i}`}
                className="h-10 w-10 grid place-items-center hairline text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 font-sans text-xs text-white/40">
          <div data-testid="footer-info">
            {footerInfo}
          </div>
          <div className="tracking-[0.2em] uppercase">
            beyondthestageacademy.me
          </div>
        </div>
      </div>
    </footer>
  );
}
